/**
 * Pure derivation of the composer's placeholder copy from the live-agent
 * handoff phase (prompt-input-bar.tsx's textarea, wired from page.tsx).
 * Standalone function — no React — so the phase→copy mapping is
 * unit-tested without mounting the composer (mirrors
 * presence-ribbon-text.ts's precedent for this same kind of small
 * phase→copy derivation).
 *
 * Deliberately keys ONLY on `phase`, never on ChatContext's `uiState`:
 * COLLECTING and TIMED_OUT both leave uiState at CHAT (see HandoffPhase's
 * JSDoc in types/chat.ts, and useAssistantConnection.ts's
 * CONTACT_INFO_REQUESTED / HANDOFF_TIMEOUT cases, which never set
 * UIState.HANDOFF for those two) — gating this on `uiState === HANDOFF`
 * would silently miss both phases. (They map to DIFFERENT copy below,
 * though — see the TIMED_OUT case's comment for why it's NOT grouped with
 * COLLECTING/WAITING despite both leaving uiState alone.)
 */
import { HandoffPhase } from '@/types/chat';

const DEFAULT_PLACEHOLDER = 'Ask anything';
const HANDOFF_PLACEHOLDER = 'Ask anything — the team will see it too';
/** Fallback when CONNECTED somehow carries no agentDisplayName — mirrors
 *  presence-ribbon.tsx / contact-summary-card.tsx's own defensive fallbacks
 *  for the same (in-practice-unreachable) gap. */
const DEFAULT_AGENT_LABEL = 'the team';

export function getComposerPlaceholder(
  phase: HandoffPhase | null | undefined,
  agentDisplayName?: string
): string {
  switch (phase) {
    case HandoffPhase.CONNECTED:
      return `Message ${agentDisplayName || DEFAULT_AGENT_LABEL}…`;
    case HandoffPhase.COLLECTING:
    case HandoffPhase.WAITING:
      return HANDOFF_PLACEHOLDER;
    default:
      // ENDED, TIMED_OUT, null, undefined — Sara is the only one reading
      // anything typed now, so HANDOFF_PLACEHOLDER's "the team will see it
      // too" would be a false promise here. TIMED_OUT used to share that
      // copy with COLLECTING/WAITING, but send-routing.ts's allow-list only
      // relays to a human during WAITING/CONNECTED — HANDOFF_TIMEOUT sets
      // uiState back to CHAT and phase to TIMED_OUT, so a reply typed after
      // a timeout goes to Sara alone. Falling back to the plain default
      // rather than inventing new copy is deliberate: "Ask anything" was
      // already true for this phase, and the recovery panel
      // (HandoffTimeoutPanel) is what actually offers to reach a human from
      // here, not the composer.
      return DEFAULT_PLACEHOLDER;
  }
}
